import {
  API_TYPES,
  createContractorBadgeVerificationBodySchema,
  type CreateContractorBadgeVerificationResponse,
  createContractorBadgeVerificationResponseSchema,
  type ShiftId,
} from "@clipboard-health/contract-backend-main";
import { post } from "@src/appV2/api/api";
import { APP_V2_APP_EVENTS } from "@src/appV2/lib/analytics";
import {
  useMutation,
  type UseMutationOptions,
  type UseMutationResult,
} from "@tanstack/react-query";
import { type AxiosError } from "axios";

import { CONTRACTOR_BADGE_VERIFICATIONS_URL } from "../constants";

interface CreateContractorBadgeVerificationParams {
  shiftId: string;
}

type Options = UseMutationOptions<
  CreateContractorBadgeVerificationResponse,
  AxiosError,
  CreateContractorBadgeVerificationParams
>;

/**
 * Hook to create a contractor badge verification record.
 *
 * This endpoint is idempotent - if a verification already exists for the
 * shift+worker combination, it returns a new presigned upload URL for
 * the existing record.
 *
 * @returns The verification record with a presigned S3 upload URL
 */
export function useCreateContractorBadgeVerification(
  options: Options = {}
): UseMutationResult<
  CreateContractorBadgeVerificationResponse,
  AxiosError,
  CreateContractorBadgeVerificationParams
> {
  return useMutation({
    mutationFn: async ({ shiftId }: CreateContractorBadgeVerificationParams) => {
      const response = await post({
        url: CONTRACTOR_BADGE_VERIFICATIONS_URL,
        data: {
          data: {
            type: API_TYPES.contractorBadgeVerification,
            attributes: {
              resourceType: "jpeg",
            },
            relationships: {
              shift: {
                data: {
                  type: API_TYPES.shift,
                  id: shiftId as ShiftId,
                },
              },
            },
          },
        },
        requestSchema: createContractorBadgeVerificationBodySchema,
        responseSchema: createContractorBadgeVerificationResponseSchema,
      });

      // Zod parsing strips branded types, so we cast back to the expected type
      return response.data as CreateContractorBadgeVerificationResponse;
    },
    meta: {
      logErrorMessage: APP_V2_APP_EVENTS.BADGE_VERIFICATION_CREATE_FAILURE,
      ...options.meta,
    },
    ...options,
  });
}
